/**
 * PEAR console — per-organisation settings.
 * Flat Aurora (Zana violet) restyle of the existing page. Same handlers;
 * staff test tools sit in a separate STAFF ONLY panel, not inside Subscription.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { AddonId } from './addonProviders';
import AddonConnector, { OrgAddon, addonStatusLabel } from './AddonConnector';
import SubscriptionManager from './SubscriptionManager';
import { CustomToast } from './customToast';
import { isCargoOrg, orgTypeBadgeLabel } from '../fleet/fleetConstants';
import { runAddonSync, SyncOrg } from '../services/addonSync';
import PromotionCard from './PromotionCard';
import './OrgSettingsPage.css';
import ZanaCapPanel from '../console/ZanaCapPanel';

type AnyOrg = Record<string, any> & { code: string; name?: string; addons?: Record<string, OrgAddon> };

const PAGE = '#FAF9FE';
const INK = '#1B1533';
const INK2 = '#5A5470';
const MUTED = '#8E86A8';
const BORDER = '#E9E5F3';
const BORDER_V = '#DED8EE';
const TINT = '#F1ECFE';
const PANEL = '#FAF9FE';
const ACCENT_TEXT = '#5B4FA8';
const GRAD = 'linear-gradient(135deg, #9B7CFF, #6F4FF0)';
const SUCCESS = '#17795A';
const SUCCESS_BG = '#E7FAF1';
const SUCCESS_BD = '#C6EEDD';
const WARN = '#8A5F14';
const WARN_BG = '#FFFBF4';
const WARN_BD = '#E8CFA0';
const WARN_CHIP = '#FBEBD2';

const VISIBLE_ADDONS: { key: AddonId; label: string; icon: string }[] = [
  { key: 'gps', label: 'GPS sensor', icon: '📍' },
  { key: 'fuel', label: 'Fuel sensor', icon: '⛽' },
  { key: 'fees', label: 'School fees', icon: '📚' },
];

function fmtDate(value: any): string {
  if (!value) return '—';
  let d: Date | null = null;
  if (typeof value?.toDate === 'function') d = value.toDate();
  else if (value instanceof Date) d = value;
  else if (typeof value === 'string') d = new Date(value);
  else if (value?.seconds) d = new Date(value.seconds * 1000);
  if (!d || isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
}

function capitalize(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "14 minutes ago" — a connection's freshness is measured in minutes, and
 *  fmtDate's day granularity cannot tell a live feed from a dead one. */
function fmtAgo(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms)) return 'never';
  const gap = Date.now() - ms;
  if (gap < 90_000) return 'just now';
  const mins = Math.round(gap / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * What the connection is actually doing right now, from the `addonHealth` doc
 * the backend sweep maintains.
 *
 * This exists because "Connected & verified" is not a live status — it records a
 * test that passed once, possibly months ago, and stayed green while an API key
 * was rotated and the feed went silent. The chip below reports the last time
 * real data arrived, which is the only claim worth making.
 */
function healthChip(h: any): { text: string; fg: string; bg: string } | null {
  if (!h) return { text: 'awaiting first sync', fg: MUTED, bg: '#F3F1FA' };
  const state = h.state as string | undefined;
  const last = Number(h.lastSuccessAtMs) || null;
  if (state === 'healthy') {
    return { text: `data ${fmtAgo(last)}`, fg: SUCCESS, bg: SUCCESS_BG };
  }
  if (state === 'degraded') {
    return { text: `retrying — last data ${fmtAgo(last)}`, fg: WARN, bg: WARN_BG };
  }
  if (state === 'down') {
    const fails = Number(h.consecutiveFailures) || 0;
    return { text: `no data ${fmtAgo(last)} (${fails} failed attempts)`, fg: '#B3261E', bg: '#FDECEA' };
  }
  return null;
}

function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        width: 48,
        height: 27,
        borderRadius: 999,
        background: on ? GRAD : '#DCD8E6',
        border: 'none',
        padding: 3,
        display: 'flex',
        justifyContent: on ? 'flex-end' : 'flex-start',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 21,
          height: 21,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(27,21,51,0.24)',
          display: 'block',
        }}
      />
    </button>
  );
}

function addonIconTile(key: AddonId, on: boolean, connectedVerified: boolean): React.CSSProperties {
  let bg = '#F4F1FA';
  let color = MUTED;
  if (connectedVerified) {
    bg = SUCCESS_BG;
    color = SUCCESS;
  } else if (on) {
    bg = TINT;
    color = '#6F4FF0';
  }
  return {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: bg,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    fontSize: 18,
    color,
  };
}

export default function OrgSettingsPage() {
  const { orgCode = '' } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState<AnyOrg | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSub, setShowSub] = useState(false);
  const [debugCoupon, setDebugCoupon] = useState<{ code: string; expiresAt: number } | null>(null);
  const [insights, setInsights] = useState<Record<string, any>>({});
  const [health, setHealth] = useState<Record<string, any>>({});
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [couponCopied, setCouponCopied] = useState(false);

  useEffect(() => {
    if (!orgCode) return;
    const unsub = onSnapshot(doc(db, 'organizations', orgCode), (snap) => {
      setOrg(snap.exists() ? ({ ...(snap.data() as any), code: snap.id }) : null);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [orgCode]);

  useEffect(() => {
    if (!orgCode) return;
    const unsubs = VISIBLE_ADDONS.map(({ key }) =>
      onSnapshot(doc(db, 'addonInsights', `${orgCode}__${key}`), (snap) => {
        setInsights((prev) => ({ ...prev, [key]: snap.exists() ? snap.data() : null }));
      }, () => { /* ignore */ })
    );
    return () => unsubs.forEach((u) => u());
  }, [orgCode]);

  // Connection health, written by the backend sweep. There is no poller here any
  // more: the sweep runs on the 15-minute cron whether or not this page is open,
  // which is the whole point — it used to be the ONLY thing running it.
  useEffect(() => {
    if (!orgCode) return;
    const unsubs = VISIBLE_ADDONS.map(({ key }) =>
      onSnapshot(doc(db, 'addonHealth', `${orgCode}__${key}`), (snap) => {
        setHealth((prev) => ({ ...prev, [key]: snap.exists() ? snap.data() : null }));
      }, () => { /* ignore */ })
    );
    return () => unsubs.forEach((u) => u());
  }, [orgCode]);

  const isCargo = isCargoOrg(org?.productType);
  const visible = VISIBLE_ADDONS.filter((a) => !(a.key === 'fees' && isCargo));

  async function toggleAddon(key: AddonId, next: boolean) {
    try {
      await updateDoc(doc(db, 'organizations', orgCode), {
        [`addons.${key}.wanted`]: next,
        [`addons.${key}.status`]: next ? 'default' : 'off',
        [`addons.${key}.verified`]: false,
        updatedAt: new Date(),
      });
    } catch {
      CustomToast.show('Could not update add-on. Try again.', 'error');
    }
  }

  async function syncNow(key: AddonId) {
    if (!org) return;
    setSyncing((s) => ({ ...s, [key]: true }));
    // The backend runs the sweep; this only asks it to run one now. It resolves
    // either way rather than throwing, and a failed sync leaves the previous
    // comparison on screen instead of blanking it.
    const result = await runAddonSync(org as SyncOrg, key);
    CustomToast.show(result.message, result.ok ? 'success' : 'error');
    setSyncing((s) => ({ ...s, [key]: false }));
  }

  async function setTestSubscription(mode: 'grace' | 'lockout' | 'restore') {
    if (!orgCode) return;
    try {
      if (mode === 'grace') {
        await updateDoc(doc(db, 'organizations', orgCode), {
          expirationDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
          gracePeriodUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          subscriptionStatus: 'expired', hasActiveSubscription: false,
        });
        CustomToast.show('⏰ Set to grace period', 'info');
      } else if (mode === 'lockout') {
        await updateDoc(doc(db, 'organizations', orgCode), {
          expirationDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
          gracePeriodUntil: new Date(Date.now() - 24 * 60 * 60 * 1000),
          subscriptionStatus: 'expired', hasActiveSubscription: false,
        });
        CustomToast.show('🔒 Set to locked (past grace)', 'info');
      } else {
        await updateDoc(doc(db, 'organizations', orgCode), {
          expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          gracePeriodUntil: null, subscriptionStatus: 'active',
          hasActiveSubscription: true, isActive: true,
        });
        CustomToast.show('✅ Subscription restored (30 days)', 'success');
      }
    } catch {
      CustomToast.show('Could not update subscription', 'error');
    }
  }

  async function generateDebugCoupon() {
    const code = `PEARTEST-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const expiresMs = Date.now() + 30 * 60 * 1000;
    try {
      await setDoc(doc(db, 'debugCoupons', code), {
        code,
        amountKES: 25,
        used: false,
        createdAt: new Date(),
        expiresAt: Timestamp.fromMillis(expiresMs),
        createdFor: orgCode,
      });
      setDebugCoupon({ code, expiresAt: expiresMs });
      CustomToast.show('Test coupon created — valid 30 minutes, one use.', 'success');
    } catch {
      CustomToast.show('Could not create coupon. Try again.', 'error');
    }
  }

  async function copyCoupon() {
    if (!debugCoupon) return;
    try {
      await navigator.clipboard.writeText(debugCoupon.code);
      setCouponCopied(true);
      window.setTimeout(() => setCouponCopied(false), 1600);
    } catch { /* ignore */ }
  }

  if (loading) {
    return <div style={{ padding: 40, color: MUTED, fontFamily: 'Geist, system-ui, sans-serif' }}>Loading organization…</div>;
  }
  if (!org) {
    return (
      <div style={{ padding: 40, fontFamily: 'Geist, system-ui, sans-serif', color: INK }}>
        <button
          type="button"
          onClick={() => navigate(`/admin?tab=organizations&org=${encodeURIComponent(orgCode)}`)}
          style={{ marginBottom: 16, background: 'none', border: 'none', color: ACCENT_TEXT, cursor: 'pointer', fontWeight: 700 }}
        >
          ← Back to organizations
        </button>
        <div style={{ color: '#C7404C' }}>Organization <b>{orgCode}</b> not found.</div>
      </div>
    );
  }

  const statusRaw = (org.subscriptionStatus || 'active').toString();
  const statusActive = statusRaw.toLowerCase() === 'active' || !!org.hasActiveSubscription;
  const plan = (org.subscriptionPlan || 'pilot').toString();
  const billing = (org.billingPeriod || 'term').toString();
  const expires = fmtDate(org.expirationDate);
  const grace = fmtDate(org.gracePeriodUntil);

  let subBlurb = 'Subscription state is shown in the tiles above.';
  if (statusActive && grace === '—') {
    subBlurb = 'Paid through the current term. The renewal reminder goes out 14 days before expiry.';
  } else if (grace !== '—') {
    subBlurb = `In grace until ${grace}. Restore or renew before lockout.`;
  } else if (!statusActive) {
    subBlurb = 'Subscription is not active — use PEAR tools carefully on live orgs.';
  }

  const tiles: { k: string; v: React.ReactNode }[] = [
    { k: 'PLAN', v: capitalize(plan) },
    { k: 'BILLING', v: capitalize(billing) },
    {
      k: 'STATUS',
      v: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusActive ? '#34D399' : '#F59E0B' }} />
          <span style={{ color: statusActive ? SUCCESS : WARN }}>{capitalize(statusRaw)}</span>
        </div>
      ),
    },
    { k: 'EXPIRES', v: expires },
    { k: 'GRACE UNTIL', v: <span style={{ color: grace === '—' ? MUTED : INK }}>{grace}</span> },
    {
      k: isCargo ? 'VEHICLES' : 'CHILDREN',
      v: String((isCargo ? org.plannedBuses : org.plannedChildren) ?? '—'),
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAGE,
        fontFamily: 'Geist, system-ui, sans-serif',
        color: INK,
      }}
    >
      <div
        style={{
          height: 58,
          background: '#fff',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 30px',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(`/admin?tab=organizations&org=${encodeURIComponent(orgCode)}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13.5,
            fontWeight: 700,
            color: INK,
            padding: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d="M10 12L6 8l4-4" stroke={INK2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to organizations
        </button>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 24,
            padding: '0 10px',
            borderRadius: 999,
            background: TINT,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.5px',
            color: ACCENT_TEXT,
          }}
        >
          PEAR ADMIN
        </span>
      </div>

      <div style={{ maxWidth: 940, margin: '0 auto', padding: '30px 30px 60px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, paddingBottom: 20 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
            <span
              style={{
                fontFamily: 'Instrument Serif, Georgia, serif',
                fontSize: 30,
                letterSpacing: '-0.9px',
                lineHeight: 1.04,
              }}
            >
              {org.name || org.code}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 23,
                  padding: '0 10px',
                  borderRadius: 999,
                  background: TINT,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  color: ACCENT_TEXT,
                }}
              >
                {(orgTypeBadgeLabel(org.productType) || 'Organisation').toUpperCase()}
              </span>
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.6px',
                  color: INK2,
                }}
              >
                {org.code}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 11,
          }}
        >
          {tiles.map((t) => (
            <div
              key={t.k}
              style={{
                padding: '14px 15px',
                borderRadius: 14,
                background: '#fff',
                border: `1px solid ${BORDER}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.9px', color: '#6E6788' }}>{t.k}</span>
              <span
                style={{
                  fontFamily: 'Instrument Serif, Georgia, serif',
                  fontSize: 17,
                  letterSpacing: '-0.3px',
                  textTransform: typeof t.v === 'string' ? 'capitalize' : undefined,
                }}
              >
                {t.v}
              </span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <ZanaCapPanel orgCode={org.code} />
        </div>

        {!isCargo && (
          <div style={{ marginTop: 14 }}>
            <PromotionCard orgCode={orgCode} educationSystem={org.educationSystem} />
          </div>
        )}

        {/* Add-ons */}
        <div
          style={{
            marginTop: 16,
            borderRadius: 18,
            background: '#fff',
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '20px 22px 16px' }}>
            <span
              style={{
                fontFamily: 'Instrument Serif, Georgia, serif',
                fontSize: 18,
                letterSpacing: '-0.4px',
                display: 'block',
              }}
            >
              Add-ons &amp; integrations
            </span>
            <p style={{ margin: '5px 0 0', fontSize: 13.5, lineHeight: 1.55, color: INK2, maxWidth: 660 }}>
              Toggle a sensor or module on, then connect the provider the organisation uses. Until a provider is connected
              the app runs in its normal mode — comparisons only start once connected.
            </p>
          </div>

          {visible.map(({ key, label, icon }) => {
            const addon: OrgAddon = (org.addons?.[key] as OrgAddon) || { wanted: false, status: 'off' };
            const on = !!addon.wanted;
            const connectedVerified = addon.status === 'connected' && !!addon.verified;
            const ins = insights[key];
            return (
              <div key={key} style={{ borderTop: '1px solid #F1EFF7', padding: '16px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={addonIconTile(key, on, connectedVerified)}>{icon}</div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {connectedVerified && (
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34D399' }} />
                      )}
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: connectedVerified ? 600 : 500,
                          color: connectedVerified ? SUCCESS : MUTED,
                        }}
                      >
                        {addonStatusLabel(addon.status, addon.verified)}
                      </span>
                    </div>
                  </div>
                  <Switch on={on} onClick={() => void toggleAddon(key, !on)} />
                </div>

                {on && (
                  <div style={{ marginTop: 12 }}>
                    <AddonConnector orgCode={orgCode} addonKey={key} addon={addon} />
                  </div>
                )}

                {connectedVerified && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: '15px 17px',
                      borderRadius: 14,
                      background: PANEL,
                      border: '1px solid #EDEAF6',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, paddingBottom: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 800 }}>Insights</span>
                      {(() => {
                        const chip = healthChip(health[key]);
                        if (!chip) return null;
                        return (
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: chip.fg,
                              background: chip.bg,
                              borderRadius: 999,
                              padding: '2px 9px',
                            }}
                          >
                            {chip.text}
                          </span>
                        );
                      })()}
                      {health[key]?.lastError && (
                        <span style={{ fontSize: 11.5, color: MUTED, flexBasis: '100%' }}>
                          {String(health[key].lastError)}
                        </span>
                      )}
                      {ins?.match && ins.match.vendorCount > 0 && ins.match.matched < ins.match.busCount && (
                        <span style={{ fontSize: 11.5, color: WARN, flexBasis: '100%' }}>
                          Matching {ins.match.matched} of {ins.match.busCount} vehicles by number plate.
                          {ins.match.unmatchedBuses?.length
                            ? ` Not found in the provider feed: ${ins.match.unmatchedBuses.join(', ')}.`
                            : ''}
                          {ins.match.unmatchedVendor?.length
                            ? ` In the feed but not in PEAR: ${ins.match.unmatchedVendor.join(', ')}.`
                            : ''}
                        </span>
                      )}
                      <div style={{ flex: 1 }} />
                      <button
                        type="button"
                        onClick={() => void syncNow(key)}
                        disabled={!!syncing[key]}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          height: 28,
                          padding: '0 12px',
                          borderRadius: 999,
                          background: TINT,
                          border: 'none',
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: ACCENT_TEXT,
                          cursor: syncing[key] ? 'default' : 'pointer',
                        }}
                      >
                        {syncing[key] ? 'Syncing…' : 'Sync now'}
                      </button>
                    </div>
                    <InsightsBody addonKey={key} data={ins} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Subscription + PEAR tools side by side when wide */}
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 16,
            flexWrap: 'wrap',
            alignItems: 'stretch',
          }}
        >
          <div
            style={{
              flex: '1 1 320px',
              borderRadius: 18,
              background: '#fff',
              border: `1px solid ${BORDER}`,
              padding: '20px 22px',
            }}
          >
            <span
              style={{
                fontFamily: 'Instrument Serif, Georgia, serif',
                fontSize: 18,
                letterSpacing: '-0.4px',
                display: 'block',
              }}
            >
              Subscription
            </span>
            <p style={{ margin: '5px 0 14px', fontSize: 13.5, lineHeight: 1.55, color: INK2 }}>
              {capitalize(plan)} · billed {billing} · expires {expires}
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '13px 15px',
                borderRadius: 14,
                background: statusActive ? SUCCESS_BG : WARN_BG,
                border: `1px solid ${statusActive ? SUCCESS_BD : WARN_BD}`,
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1.2 }}>{statusActive ? '✓' : '⚠'}</span>
              <span
                style={{
                  flex: 1,
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: statusActive ? '#12694A' : WARN,
                }}
              >
                {subBlurb}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 9, paddingTop: 14, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowSub(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 40,
                  padding: '0 18px',
                  borderRadius: 999,
                  background: GRAD,
                  border: 'none',
                  boxShadow: '0 12px 28px -14px rgba(111,79,240,0.8)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Open manager
              </button>
            </div>
          </div>

          <div
            style={{
              width: 350,
              flex: '1 1 280px',
              maxWidth: '100%',
              borderRadius: 18,
              background: WARN_BG,
              border: `1px dashed ${WARN_BD}`,
              padding: '20px 22px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontSize: 16 }} aria-hidden>🛠</span>
              <span
                style={{
                  flex: 1,
                  fontFamily: 'Instrument Serif, Georgia, serif',
                  fontSize: 15.5,
                  letterSpacing: '-0.3px',
                  color: WARN,
                }}
              >
                PEAR tools
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 21,
                  padding: '0 9px',
                  borderRadius: 999,
                  background: WARN_CHIP,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  color: WARN,
                }}
              >
                STAFF ONLY
              </span>
            </div>
            <p style={{ margin: '6px 0 13px', fontSize: 12.5, lineHeight: 1.5, color: WARN }}>
              These write real subscription state on a live organisation.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {(
                [
                  { mode: 'grace' as const, label: 'Grace period' },
                  { mode: 'lockout' as const, label: 'Lockout' },
                  { mode: 'restore' as const, label: 'Restore' },
                ] as const
              ).map((b) => (
                <button
                  key={b.mode}
                  type="button"
                  onClick={() => void setTestSubscription(b.mode)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    height: 32,
                    padding: '0 12px',
                    borderRadius: 999,
                    background: '#fff',
                    border: '1px solid #EFDFC2',
                    fontSize: 12,
                    fontWeight: 700,
                    color: WARN,
                    cursor: 'pointer',
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>

            <div style={{ marginTop: 14, paddingTop: 13, borderTop: `1px dashed ${WARN_BD}` }}>
              <span style={{ display: 'block', fontSize: 11, fontWeight: 800, letterSpacing: '0.8px', color: WARN }}>
                PAYMENT TESTING
              </span>
              {debugCoupon && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 13,
                    marginTop: 10,
                    padding: '13px 15px',
                    borderRadius: 14,
                    background: '#fff',
                    border: '1px solid #EFDFC2',
                  }}
                >
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span
                      style={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 19,
                        fontWeight: 800,
                        letterSpacing: '1.5px',
                        color: '#12694A',
                      }}
                    >
                      {debugCoupon.code}
                    </span>
                    <span style={{ fontSize: 11.5, color: WARN }}>
                      One use · expires {new Date(debugCoupon.expiresAt).toLocaleTimeString('en-KE')} · checkout drops to KES 25
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void copyCoupon()}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                      color: WARN,
                      flexShrink: 0,
                    }}
                  >
                    {couponCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => void generateDebugCoupon()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: 36,
                  marginTop: 9,
                  borderRadius: 999,
                  background: '#fff',
                  border: '1px solid #EFDFC2',
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: WARN,
                  cursor: 'pointer',
                }}
              >
                {debugCoupon ? 'Generate a new 30-min coupon' : 'Generate 30-min test coupon (→ KES 25)'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showSub && (
        <SubscriptionManager orgCode={orgCode} orgData={org as any} onClose={() => setShowSub(false)} />
      )}
    </div>
  );
}

function InsightsBody({ addonKey, data }: { addonKey: AddonId; data: any }) {
  if (!data) return <div style={{ fontSize: 12, color: MUTED }}>No data yet — click “Sync now”.</div>;

  if (addonKey === 'fuel' && data.fuel) {
    const rows = data.fuel.perBus || [];
    let sensorSum = 0;
    let driverSum = 0;
    let n = 0;
    for (const r of rows) {
      const s = Number(r.sensorEffKmPerL);
      const d = Number(r.manualEffKmPerL);
      if (Number.isFinite(s) && Number.isFinite(d) && s > 0 && d > 0) {
        sensorSum += s;
        driverSum += d;
        n += 1;
      }
    }
    const sensorAvg = n ? sensorSum / n : null;
    const driverAvg = n ? driverSum / n : null;
    const variancePct =
      sensorAvg != null && driverAvg != null && driverAvg > 0
        ? Math.round(((sensorAvg - driverAvg) / driverAvg) * 100)
        : null;

    return (
      <div>
        {sensorAvg != null && driverAvg != null && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <InsightStat value={`${sensorAvg.toFixed(1)}`} unit=" km/l" label="Sensor average" />
            <InsightStat value={`${driverAvg.toFixed(1)}`} unit=" km/l" label="Driver-reported" />
            <InsightStat
              value={variancePct == null ? '—' : `${variancePct > 0 ? '+' : ''}${variancePct}%`}
              label="Variance"
              accent={variancePct != null && Math.abs(variancePct) >= 10 ? SUCCESS : undefined}
            />
          </div>
        )}
        <div style={{ fontSize: 12, color: INK2, marginBottom: 6 }}>
          {data.fuel.flaggedCount || 0} bus(es) flagged for fuel divergence.
        </div>
        {rows.map((r: any) => (
          <div
            key={r.busId}
            style={{
              fontSize: 12,
              padding: '5px 0',
              borderTop: '1px dashed #EDEAF6',
              color: r.flagged ? '#b45309' : INK2,
            }}
          >
            <b>{r.busName}</b> · driver {r.driverLiters}L vs sensor {r.sensorLiters}L · {r.kmTravelled}km · eff{' '}
            {r.manualEffKmPerL}/{r.sensorEffKmPerL} km/L {r.flagged ? '· ⚠ flagged' : ''}
          </div>
        ))}
      </div>
    );
  }
  if (addonKey === 'gps' && data.gps) {
    const rows = data.gps.perBus || [];
    return (
      <div>
        {rows.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>No buses with trip distance yet.</div>}
        {rows.map((r: any) => (
          <div
            key={r.busId}
            style={{
              fontSize: 12,
              padding: '5px 0',
              borderTop: '1px dashed #EDEAF6',
              color: r.flagged ? '#b45309' : INK2,
            }}
          >
            <b>{r.busName}</b> · sensor {r.sensorOdometerKm}km vs phone {r.phoneOdometerKm}km · {r.discrepancyPct}% · use{' '}
            {r.recommendedSource} {r.flagged ? '· ⚠ flagged' : ''}
          </div>
        ))}
      </div>
    );
  }
  if (addonKey === 'fees' && data.fees) {
    return (
      <div style={{ fontSize: 12.5, color: INK2 }}>
        {data.fees.matchedCount || 0} matched · {data.fees.unmatchedCount || 0} unmatched (left untouched) ·{' '}
        {data.fees.unpaidCount || 0} unpaid · {data.fees.suspendedCount || 0} child(ren) suspended from pickup.
      </div>
    );
  }
  return <div style={{ fontSize: 12, color: MUTED }}>No data yet — click “Sync now”.</div>;
}

function InsightStat({
  value,
  unit,
  label,
  accent,
}: {
  value: string;
  unit?: string;
  label: string;
  accent?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span
        style={{
          fontFamily: 'Instrument Serif, Georgia, serif',
          fontSize: 22,
          letterSpacing: '-0.6px',
          lineHeight: 1,
          color: accent || INK,
        }}
      >
        {value}
        {unit ? <span style={{ fontSize: 12, fontWeight: 700, color: INK2 }}>{unit}</span> : null}
      </span>
      <span style={{ fontSize: 11.5, color: INK2 }}>{label}</span>
    </div>
  );
}
